import { useRecoilValue } from 'recoil';
import { useTranslation } from 'react-i18next';
import { isMobile } from 'react-device-detect';
import { DelimitedArrayParam, BooleanParam, NumberParam, StringParam, useQueryParams } from 'use-query-params';
import { ExportType } from 'models/StockModel';
import { Option } from 'models/common';
import carListFamily from 'recoil/atoms/carListFamily';
import loadingYardList from 'recoil/selectors/loadingYardList';
import { CustomSet } from 'utils/common';


const useCarsFilter = (silent = false) => {
    const {t} = useTranslation('cars');
    const allCars = useRecoilValue(carListFamily('pending'));
    const yards = useRecoilValue(loadingYardList);
    const [filter, setFilter] = useQueryParams({
        yard: NumberParam,
        shipper: NumberParam,
        voyage: NumberParam,
        state: StringParam,
        photos: BooleanParam,
        docs: BooleanParam,
        vehicleType: DelimitedArrayParam,
    });

    const changeFilter = (key: string, value: string | number | string[]) => {
        !isMobile && setFilter(old => ({
            ...old,
            [key]: value === '' || (Array.isArray(value) && value.length === 0) ? undefined : value
        }))
    }

    const carsByYard = allCars.filter(c =>
        filter.yard === undefined || (filter.yard === 0 && !c.yard) || filter.yard === c.yard?.id
    );

    const cars = carsByYard.filter(c =>
            (filter.voyage === undefined || (filter.voyage === 0 && !c.voyage) || filter.voyage === c.voyage?.id)
            && (filter.shipper === undefined || filter.shipper === c.company.id)
            && (filter.photos === undefined || (filter.photos ? c.photoCount : !c.photoCount))
            && (filter.docs === undefined || (filter.docs ?
                    (c.documents.exportCertificate || c.documents.vehicleInvoice) :
                    !(c.documents.exportCertificate || c.documents.vehicleInvoice))
                )
            && (
                filter.state === undefined ||
                (filter.state === 'unknown' && !c.exportType) ||
                (filter.state === 'whole' && c.exportType === ExportType.Car) ||
                (filter.state === 'dis' && c.exportType !== ExportType.Car) ||
                (filter.state === 'dmg' && c.damaged)
            )
            && (!filter.vehicleType || filter.vehicleType.includes(c.vehicleType as string))
    );

    let carCountByYard: Record<number, number> = {};
    let yardFilterOptions: Option[] = [];
    let voyageFilterOptions: Option[] = [];
    let companyFilterOptions: Option[] = [];

    if (!silent) {
        carCountByYard = Object.fromEntries(yards.map(y => [y.id, 0]));
        carCountByYard[0] = 0;
        allCars.forEach(c => {
            carCountByYard[c.yard?.id || 0]++;
        });

        yardFilterOptions = [
            {value: '', label: `${t('all')} (${allCars.length})`},
            ...yards.map(yard => ({value: yard.id, label: `${yard.port.name} ${yard.name} (${carCountByYard[yard.id]})`})),
            {value: 0, label: `${t('no_yard')} (${carCountByYard[0]})`},
        ];

        const voyages = [...new CustomSet(
            (a, b) => a.id === b.id,
            carsByYard
                .filter(c => c.voyage)
                .map(c => c.voyage!)
        )];

        voyageFilterOptions = [
            {value: '', label: t('any')},
            {value: 0, label: t('no_voyage')},
            ...voyages.map(v => ({value: v.id, label: v.number}))
                .sort((a, b) => b.value - a.value)
        ];

        const companies = [...new CustomSet(
            (a, b) => a.id === b.id,
            carsByYard
                .filter(c => (filter.voyage === undefined || (filter.voyage === 0 && !c.voyage) || filter.voyage === c.voyage?.id))
                .map(c => c.company)
        )];

        companyFilterOptions = [
            {value: '', label: t('any')},
            ...companies.map(c => ({value: c.id, label: c.name}))
                .sort((a, b) => a.label.localeCompare(b.label))
        ];
    }

    return {
        filter,
        changeFilter,
        cars,
        yardFilterOptions,
        voyageFilterOptions,
        companyFilterOptions,
    };
};

export default useCarsFilter;
